import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IPostProjektStatusResponse, IProjektStatus } from 'fa-kt.types';
import { ProjektStatiService } from '../../../query';
import { PostProjektStatusCommandService } from '../service';

interface IFormValue {
  projektStatus?: IProjektStatus;
}

@Component({
  selector: 'fa-kt-post-projekt-status',
  templateUrl: './post-projekt-status.component.html',
  styleUrls: ['./post-projekt-status.component.css'],
})
export class PostProjektStatusCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostProjektStatusResponse> = new EventEmitter<IPostProjektStatusResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public projektStati: ProjektStatiService,
    public postProjektStatus: PostProjektStatusCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.PROJEKTE.Commands.POST_PROJEKT_STATUS;
    this.model = sitemap.PROJEKTE.Models.PROJEKT_STATUS;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      projektStatus: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
        color: [null, []],
        icon: [null, []],
        order: [null, []],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let projektStatus = {
      id: null,
      name: null,
      beschreibung: null,
      order: 0,
      color: '',
      icon: '',
    };
    if (value && value.projektStatus) {
      projektStatus = {
        ...projektStatus,
        ...cleanObject(value.projektStatus),
      };
    }
    this.form.patchValue({ projektStatus });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.projektStatus) {
        payload.projektStatus = cleanObject(payload.projektStatus);
      }
      const response: IPostProjektStatusResponse | undefined = await this.postProjektStatus.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.projektStatus.get('id');
  }

  get name() {
    return this.form.controls.projektStatus.get('name');
  }

  get beschreibung() {
    return this.form.controls.projektStatus.get('beschreibung');
  }
}
